import { createApi } from '@reduxjs/toolkit/dist/query/react';

import serverRoutes from '@app/utils/server-routes';

import {
  GPTGroupChatListObjectType,
  GPTGroupMetricListChatObject,
  GPTGroupChatObjectType,
  GptChatGroupMutationObjectType,
  GPTGroupChatListExportType,
} from '@app/interfaces/gpt-metric-chats.type';

import { ChatMetricObjType } from '@app/interfaces/pages-types/anatylics-metric.type';

import { GptGroupTestResultType, GptGroupManualCalcResult } from '@app/interfaces/gpt-metric.type';

import fileDownload from 'js-file-download';

import { baseQuery } from '../../baseQuery';

export const gptMetricChatApi = createApi({
  reducerPath: 'gptMetricChatApi',
  tagTypes: ['gptGroupsChatList', 'gptChatGroupSingle'],
  baseQuery: baseQuery,
  endpoints: (build) => ({
    getChatGptGroupsWithMetrics: build.query<
      GPTGroupMetricListChatObject,
      { params: { project_id: string; gpt_group_id?: string } }
    >({
      query: ({ params }) => ({
        url: serverRoutes.gpt_chat_group_metrics_bulk,
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.gpt_groups.map(({ gpt_group_id }) => ({
                type: 'gptGroupsChatList' as const,
                gpt_group_id,
              })),
              'gptGroupsChatList',
            ]
          : ['gptGroupsChatList'],
    }),
    getChatGptGroupsList: build.query<GPTGroupChatListObjectType, { project_id: string }>({
      query: (params) => ({
        url: serverRoutes.gpt_chat_group_bulk,
        params,
      }),
    }),
    getChatGptGroupSingle: build.query<GPTGroupChatObjectType, { gpt_group_id: string }>({
      query: (params) => ({
        url: serverRoutes.gpt_chat_group,
        params,
      }),
      providesTags: ['gptChatGroupSingle'],
    }),
    createChatGptGroup: build.mutation<GPTGroupChatObjectType, GptChatGroupMutationObjectType>({
      query: (body) => ({
        url: serverRoutes.gpt_chat_group,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['gptGroupsChatList'],
    }),
    editChatGptGroup: build.mutation<
      GPTGroupChatObjectType,
      { body: Omit<GptChatGroupMutationObjectType, 'project_id'>; params: { gpt_group_id: string } }
    >({
      query: ({ body, params }) => ({
        url: serverRoutes.gpt_chat_group,
        method: 'PUT',
        body,
        params,
      }),
      invalidatesTags: ['gptChatGroupSingle', 'gptGroupsChatList'],
    }),
    deleteChatGptGroup: build.mutation<GPTGroupChatObjectType, { gpt_group_id: string }>({
      query: (params) => ({
        url: serverRoutes.gpt_chat_group,
        method: 'DELETE',
        params,
      }),
      invalidatesTags: ['gptGroupsChatList'],
    }),
    // SINGLE METRIC MUTATIONS
    createProjectMetricGroupForChatGpt: build.mutation<
      ChatMetricObjType,
      {
        params: {
          project_id: string;
        };
        body: ChatMetricObjType;
      }
    >({
      query: ({ body, params }) => ({
        url: serverRoutes.metricProjectChat,
        method: 'POST',
        body,
        params,
      }),
      invalidatesTags: ['gptGroupsChatList'],
    }),
    editProjectMetricGroupForChatGpt: build.mutation<
      ChatMetricObjType,
      {
        params: {
          project_id: string;
          metric_id: string;
        };
        body: ChatMetricObjType;
      }
    >({
      query: ({ params, body }) => ({
        url: serverRoutes.metricProjectChat,
        method: 'PUT',
        params,
        body,
      }),
      invalidatesTags: ['gptGroupsChatList'],
    }),
    deleteProjectMetricForChatGpt: build.mutation({
      query: (params: { project_id: string; metric_id: string }) => ({
        url: serverRoutes.metricProjectChat,
        method: 'DELETE',
        params,
        redirect: 'manual',
      }),

      invalidatesTags: ['gptGroupsChatList'],
    }),
    // SINGLE METRIC MUTATIONS
    // TEST CALC METRIC
    testCalcChatGptMetricsInGroup: build.query<
      GptGroupTestResultType,
      { gpt_group_id: string; chat_id?: string }
    >({
      query: (params) => ({
        url: serverRoutes.gpt_chat_group_metrics_calc_test,
        params,
      }),
    }),

    // TEST CALC METRIC
    // manual recalc gpt group
    recalculateChatGptGroupManual: build.query<
      GptGroupManualCalcResult,
      { gpt_group_id: string; chat_id?: string }
    >({
      query: (params) => ({
        url: serverRoutes.gpt_chat_group_metrics_manual_recalc,
        params,
        method: 'PUT',
      }),
    }),
    // manual recalc gpt group
    // IMPORT&EXPORT
    exportGptChatGroup: build.query<GPTGroupChatListExportType, { gpt_group_id: string }>({
      query: (params) => ({
        url: serverRoutes.gpt_chat_group_export,
        params,
        responseHandler: async (response) => {
          const contentDisposition = response.headers.get('content-disposition');

          const buffer = await response.arrayBuffer();
          const text = new TextDecoder().decode(buffer);

          const filename =
            contentDisposition?.match(/filename="([^"]+)"/)?.[1] ||
            (JSON.parse(text) as GPTGroupChatListExportType).gpt_group.name + '.json';

          const blob = new Blob([buffer], { type: 'application/json' });
          fileDownload(blob, filename);
        },
        cache: 'no-cache',
      }),
    }),
    importGptChatGroup: build.mutation<GPTGroupChatObjectType, FormData>({
      query: (body) => ({
        url: serverRoutes.gpt_chat_group_import,
        method: 'POST',
        body,
        // headers: { 'Content-Type': 'multipart/form-data' },
      }),
      invalidatesTags: ['gptGroupsChatList'],
    }),
    // IMPORT&EXPORT
  }),
});

export const {
  useGetChatGptGroupsListQuery,
  useLazyGetChatGptGroupSingleQuery,
  useCreateChatGptGroupMutation,
  useEditChatGptGroupMutation,
  useDeleteChatGptGroupMutation,
  useGetChatGptGroupsWithMetricsQuery,
  // SINGLE METRIC MUTATIONS
  useDeleteProjectMetricForChatGptMutation,
  useCreateProjectMetricGroupForChatGptMutation,
  useEditProjectMetricGroupForChatGptMutation,
  // TEST CALC METRIC
  useLazyTestCalcChatGptMetricsInGroupQuery,
  // manual recalc Chatgpt group
  useLazyRecalculateChatGptGroupManualQuery,
  // IMPORT&EXPORT
  useLazyExportGptChatGroupQuery,
  useImportGptChatGroupMutation,
} = gptMetricChatApi;
